/* Bar Fridges Australia — model reviews.
 *
 * Real browser source, copied verbatim into assets/bfau-model-reviews.js by
 * build-reviews.js. A plain file rather than a string inside the builder,
 * because escaping it twice is what put a literal backslash into a regex and a
 * $ where a $$ belonged. Nothing here is templated; edit it as JavaScript.
 *
 * WHAT IT DOES
 *
 * Feefo keys reviews on variant SKU, so a fridge sold under six SKUs has its
 * reviews split six ways and a branded wrap of a plain cabinet usually has none
 * at all. This is the product page's review section, and it shows the MODEL's
 * reviews: this exact SKU's first, then the other SKUs on the same chassis,
 * every one labelled with the fridge it was actually written about.
 *
 * That label is the condition on which the rest is fair. A star rating is an
 * aggregate about the model and reads as one; a customer's words are about the
 * fridge that customer bought, so each says which.
 *
 * It quotes 3-, 4- and 5-star reviews only. The RATING is still Feefo's over
 * all of them -- see SHOWN below for why the aggregate and the quotes part
 * company here.
 *
 * DISPLAY ONLY. None of this may reach structured data.
 */
(function () {
  var root = document.querySelector('.bfau-mr');
  if (!root) return;
  var sku = root.getAttribute('data-sku');
  var model = root.getAttribute('data-model') || sku;
  if (!sku) return;

  /* Feefo's own endpoints, as the storefront widget builds them.
     since_period=all is load-bearing: omit it and the API answers zero reviews
     for products that plainly have them, with no error to notice.
     product_sku takes a COMMA-SEPARATED LIST and Feefo pools it, weighted.
     page and rating are real server parameters, measured 2026-08-19. There is
     NO working sort: sort=popular is accepted and ignored, sort_by and order
     break the response. Default order is newest first, which is why the one tab
     says Newest and there is no Popular beside it. */

  /* ---------------------------------------------------------------------
     Condition-prefixed stock: scratch-and-dent (SD-) and factory second (T2-).
     Ported from condition() + rootOf() in derive.js -- the browser has to ask
     the same question at runtime that the Node build asks, because these SKUs
     are NOT in the baked map and never will be reliably.

     They churn: a unit is listed when it arrives and gone when it sells, and
     the model code it wraps is different every time. A map keyed on SKU is
     stale for them the day it is built, so the chassis is derived here instead
     and looked up in the `_chassis` map that build-reviews.js emits alongside.

       SD-TW103-BC70B-ROADIE-AUS   scratch and dent, report TW103, of BC70B-ROADIE-AUS
       T2-A645-SC372W-VC           factory second,   report A645,  of SC372W-VC

     The token after the prefix names whoever wrote the damage report and has no
     product meaning, so it has to come off before the chassis is read. But a
     report code (A645, TW103, BA) is the same SHAPE as a model code -- EC68
     looks exactly like TW103 -- so BOTH readings are tried and whichever names
     a real chassis wins. Longest strip first: usually the next token IS the
     report code.
     --------------------------------------------------------------------- */
  var CONDITION = /^(?:SD|T2)-\s*/i;

  /* The chassis a model belongs to, read off the code alone -- same regex as
     rootOf() in derive.js. SC70-B-CCR and HUS-SC70-SS both reduce to their
     chassis; the trailing design code is exactly the part a new livery changes,
     so this keeps working on a SKU the map has never seen. */
  function rootOf(text) {
    var m = String(text || '').toUpperCase().replace(/^(HUS|DW|EC)-/, '');
    var hit = /^(?:([A-Z]+)-)?([A-Z]+\d+)/.exec(m);
    if (!hit) return m;
    return hit[1] ? hit[1] + '-' + hit[2] : hit[2];
  }

  /* A chassis whose review pool splits by finish -- SC70 only, and the same one
     entry as POOL_SPLIT in derive.js. The stainless SC70s are triple glazed and
     the black ones double glazed, so their reviews do not describe each other
     and the map is keyed SC70-B / SC70-SS rather than SC70. Read as a SEGMENT
     right after the model number, so a design code that merely contains the
     letters cannot answer. */
  var SPLIT = { SC70: 1 };
  function finishToken(text) {
    var seg = String(text || '').toUpperCase().replace(/^(HUS|DW|EC)-/, '').split('-');
    var i = -1;
    for (var n = 0; n < seg.length; n++) { if (/^SC70L?$/.test(seg[n])) { i = n; break; } }
    var next = i < 0 ? null : seg[i + 1];
    return next === 'B' || next === 'SS' ? next : null;
  }

  /* Every chassis key this SKU might be filed under, best reading first.

     A plain SKU gives one reading; a condition-prefixed one gives two, because
     the report code cannot be told from a model code by shape -- EC68 looks
     exactly like TW103. Longest strip first: usually the next token IS the
     report code.

     Each reading yields the SPLIT key before the bare root, so a scratch-and-
     dent SC70 lands on the side its finish token puts it rather than failing to
     find a bare "SC70" that the map does not carry. */
  function chassisKeys(sku) {
    var raw = String(sku || '').trim();
    var m = CONDITION.exec(raw);
    var readings = m
      ? [raw.slice(m[0].length).trim().replace(/^[A-Z]+\d*-\s*/i, ''), raw.slice(m[0].length).trim()]
      : [raw];
    var out = [];
    readings.forEach(function (c) {
      if (!c) return;
      var r = rootOf(c);
      if (!r) return;
      if (SPLIT[r]) {
        var t = finishToken(c);
        if (t && out.indexOf(r + '-' + t) < 0) out.push(r + '-' + t);
      }
      if (out.indexOf(r) < 0) out.push(r);
    });
    return out;
  }

  /* The SKU list to ask Feefo about.
     
     1. Exact match on the baked SKU map -- the pool build-reviews.js actually
        computed for this fridge, FUNCTIONAL gate and all. Always wins.
     2. `_solo` names the SKUs the build looked at and deliberately left without
        a pool, because no donor on the chassis is the same machine. Silence is
        the correct answer for those, so they stop here. Without this step they
        are indistinguishable from a SKU the build never saw, and 16 of them --
        SK86R-SD, SK245-SD, SG2H-SD and the like -- would take the chassis pool
        and show a heated-glass fridge's reviews on a solid-door card.
     3. The chassis map. This is what carries condition-prefixed stock (SD-/T2-)
        and any design added since the last build.
     4. Own SKU. Never an EMPTY list: Feefo answers a blank product_sku with the
        MERCHANT-WIDE score, which would put the shop's overall rating on an
        unreviewed fridge. */
  function poolFor(sku, map) {
    if (!sku) return sku;
    if (map[sku]) return map[sku];
    var solo = map._solo || [];
    if (solo.indexOf(sku) >= 0) return sku;
    var byChassis = map._chassis || {};
    var keys = chassisKeys(sku);
    for (var i = 0; i < keys.length; i++) {
      if (byChassis[keys[i]]) return byChassis[keys[i]];
    }
    return sku;
  }

  var Q = '?since_period=all&unanswered_feedback=include'
    + '&merchant_identifier=bar-fridges-australia&origin=bar-fridges-australia.com.au';
  var API = 'https://api.feefo.com/api/10/reviews/product' + Q;
  var SUMMARY = 'https://api.feefo.com/api/10/reviews/summary/product' + Q
    + '&source=on_page_product_integration';

  /* Both marks come from the shop's own CDN, not from Feefo's servers: a third
     party should not control an image on the product page. The logo is the same
     file bfau_pr_quickinfo.liquid already puts beside the product stars. */
  var FEEFO_LOGO = 'https://cdn.shopify.com/s/files/1/0682/9549/1876/files/'
    + 'Feefo_logo_grey_yellow.png?v=1724112967';
  var AWARD = 'https://cdn.shopify.com/s/files/1/0682/9549/1876/files/'
    + 'feefo-gold-service-award.svg?v=1787119246';

  var PER_PAGE = 6;      // rows on screen
  var CHUNK = 24;        // rows per request, sliced locally
  var MAX_PAGES_SHOWN = 5;

  /* The star levels this section quotes in words. 1 and 2 are not among them.
   *
   * `rating` takes a COMMA-SEPARATED LIST and Feefo pools it, the same way
   * product_sku does -- measured 2026-09-01, where rating=4,5 on the SC70 pool
   * answers 163 against the 32 and 131 the two levels answer separately. So
   * "no filter" is no longer an ABSENT parameter: it is this list, and every
   * page of rows and every count below is asked at these levels only. Nothing
   * is dropped after it arrives, which matters -- see the note in chunk() about
   * counts and offsets sliding out of step when rows are filtered locally.
   *
   * The SCORE is deliberately left alone: it stays Feefo's figure over every
   * review including the ones not quoted, because it is the same number the
   * finder's cards and the rating above the fold draw, and a recomputed one
   * would read higher than Feefo's own under Feefo's own mark. The COUNT is
   * not left alone -- it says how many reviews are here, so it has to be the
   * count of the ones that are, on all three surfaces. Which is why the line
   * under the score no longer claims the score is "based on" it: both figures
   * are true, neither is computed from the other.
   *
   * model-stars.browser.js and card-stars.browser.js carry this same list, and
   * build-reviews.js asserts all three agree -- a card reading (25) above a
   * section showing 22 is exactly the disagreement this block exists to avoid.
   */
  var SHOWN = '3,4,5';
  function isShown(level) { return SHOWN.split(',').indexOf(String(level)) >= 0; }
  function ratingParam() { return state.rating ? String(state.rating) : SHOWN; }

  var CHASSIS = '', RATING = 0, SHOWN_TOTAL = 0, SET_TOTAL = 0;
  /* How many QUOTED reviews sit at each star level, for the filter buttons.
     Free: the summary response carries 5_star..1_star beside the average, so
     the counts cost no extra request. The levels SHOWN excludes are held at 0
     here rather than carrying their real figure, because this is what the
     buttons read out and 0 is what is behind them. */
  var HIST = {};
  var state = { page: 1, rating: 0, dir: 'newest' };

  /* Two streams, read in order: this exact SKU, then every other SKU on the
   * chassis. That ordering IS the feature — someone reading reviews on a
   * SK156R-HD should see reviews of an SK156R-HD before reviews of its black
   * twin — and it cannot be asked of the API, which has no sort parameter and
   * would interleave them by date.
   *
   * Stream 0 is empty on a branded design, which has no reviews of its own, and
   * stream 1 is empty on a fridge that is the only SKU on its chassis. Neither
   * needs a special case: an empty stream contributes nothing and the other
   * carries the section.
   */
  var streams = [];
  var cache = {};        // "stream:rating:chunk" -> array of rows

  function rootOf(text) {
    var m = String(text || '').toUpperCase().replace(/^(HUS|DW|EC)-/, '');
    var hit = /^(?:([A-Z]+)-)?([A-Z]+\d+)/.exec(m);
    if (!hit) return m;
    return hit[1] ? hit[1] + '-' + hit[2] : hit[2];
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* The same stars the rating at the top of the page draws and the finder draws.
     This was span.stars, the Feefo PNG sprite from bfau-pr-single-css.liquid,
     which reads gold; then the Unicode glyph, which reads thin and amber and
     draws well under the size it is set at. It is now the shared SVG art. */
  /* The shared star art (star-css.js): a solid SVG star at Feefo's own #FFD000,
     sized in px by the stylesheet rather than by font size. Two numbers, not one
     percentage, because the row has gaps in it — 96% of a five-star row is 4.8
     stars plus a slice of gap, and at 22px that slice is visible. */
  function starStyle(r) {
    var v = Math.max(0, Math.min(5, r || 0));
    var n = Math.floor(v);
    return '--bfau-star-n:' + n + ';--bfau-star-frac:' + (v - n).toFixed(2);
  }

  function stars(r) {
    return '<span class="bfau-mr-stars" style="' + starStyle(r)
      + '" aria-hidden="true"><i></i></span>';
  }

  function when(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    /* "29 October 2020" — day first, month spelled out, which is how the review
       dates on this shop already read, and Australian, which the shop is. */
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  function url(streamIdx, chunkIdx) {
    return API + '&product_sku=' + encodeURIComponent(streams[streamIdx].skus)
      + '&page_size=' + CHUNK + '&page=' + (chunkIdx + 1)
      + '&rating=' + ratingParam();
  }

  /* One chunk of one stream, cached.
   *
   * Fetched in 24s and sliced to 6 locally rather than asking for 6 at a time,
   * because a page of the SECTION spans two streams wherever the first one's
   * length is not a multiple of six — 10 own reviews means page 2 is four of
   * ours and two of the chassis's. The API can only page by its own page_size,
   * so the alignment has to happen on this side. */
  function chunk(streamIdx, chunkIdx) {
    var key = streamIdx + ':' + state.rating + ':' + chunkIdx;
    if (cache[key]) return Promise.resolve(cache[key]);
    if (!streams[streamIdx] || !streams[streamIdx].skus) return Promise.resolve([]);
    return fetch(url(streamIdx, chunkIdx), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) {
        var meta = (j && j.summary && j.summary.meta) || {};
        streams[streamIdx].count = meta.count || 0;
        var rows = [];
        ((j && j.reviews) || []).forEach(function (rv) {
          var p = rv.products && rv.products[0];
          if (!p || !p.product) return;
          /* A rating with no comment is kept, not skipped.
             It has to be: summary.meta.count counts every review, and the page
             offsets are computed from that count, so dropping some rows here
             slides the two out of step. Measured on the SC70 — 104 own reviews
             by count, fewer with text — the drift swallowed the last two own
             reviews at the stream boundary and page 18 opened on a sibling.
             It also makes "Showing 197 reviews" literally true: all 197 are
             reachable. A comment-less row renders as stars, name, date and the
             model it was written about, which is everything Feefo holds for it. */
          if (p.product.reviews_url && !streams[streamIdx].url) {
            streams[streamIdx].url = p.product.reviews_url;
          }
          rows.push({
            rating: (p.rating && p.rating.rating) || 0,
            text: p.review,
            sku: p.product.sku,
            own: String(p.product.sku).toUpperCase() === String(sku).toUpperCase(),
            at: p.created_at,
            who: (rv.customer && rv.customer.display_name) || 'Trusted Customer'
          });
        });
        cache[key] = rows;
        return rows;
      })
      .catch(function () { return []; });
  }

  /* Rows [from, from+n) of one stream, in the API's own order, fetching however
     many chunks that spans. Sparse-then-slice so two chunks join without any
     index arithmetic at the seam. */
  function take(idx, from, n) {
    if (n <= 0 || !streams[idx] || !streams[idx].skus) return Promise.resolve([]);
    var c0 = Math.floor(from / CHUNK), c1 = Math.floor((from + n - 1) / CHUNK);
    var jobs = [];
    for (var c = c0; c <= c1; c++) jobs.push(chunk(idx, c));
    return Promise.all(jobs).then(function (chunks) {
      var flat = [];
      chunks.forEach(function (rows, k) {
        var base = (c0 + k) * CHUNK;
        rows.forEach(function (r, j) { flat[base + j] = r; });
      });
      return flat.slice(from, from + n).filter(Boolean);
    });
  }

  /* PER_PAGE rows starting at a global offset, walking the streams in order.
   *
   * "In order" is the own-SKU stream first and the rest of the chassis after,
   * always — that is the section's primary sort and the sort control does not
   * touch it. Newest/Oldest reorders WITHIN each stream, which is what a shopper
   * means by it: they want this fridge's oldest review before the chassis's
   * oldest, not the oldest review of anything.
   *
   * Oldest is done by reading each stream from its far end, because the API has
   * no sort parameter to ask for it. Position i counting back from the end is
   * source position (count - 1 - i), so a page of six is one source range read
   * and then reversed. */
  function slice(offset, want) {
    var out = [];

    function step(idx, remaining) {
      if (idx >= streams.length || out.length >= want) return Promise.resolve();
      var c = streams[idx].count || 0;
      if (remaining >= c) return step(idx + 1, remaining - c);

      var need = Math.min(want - out.length, c - remaining);
      var src = state.dir === 'oldest' ? (c - remaining - need) : remaining;
      return take(idx, src, need).then(function (rows) {
        if (state.dir === 'oldest') rows = rows.slice().reverse();
        out = out.concat(rows);
        return step(idx + 1, 0);
      });
    }

    return step(0, offset).then(function () { return out; });
  }

  function total() { return SET_TOTAL; }

  function pageButtons(pages, current) {
    if (pages < 2) return '';
    /* Five numbers with an arrow either side. The left arrow is absent on page
       one rather than disabled: a control that cannot do anything is better not
       drawn. */
    var from = Math.max(1, current - 2);
    var to = Math.min(pages, from + MAX_PAGES_SHOWN - 1);
    from = Math.max(1, to - MAX_PAGES_SHOWN + 1);
    var h = '<div class="bfau-mr-pages">';
    if (current > 1) {
      h += '<button type="button" class="bfau-mr-pg" data-page="' + (current - 1)
        + '" aria-label="Previous page">&lsaquo;</button>';
    }
    for (var i = from; i <= to; i++) {
      h += '<button type="button" class="bfau-mr-pg" data-page="' + i + '"'
        + (i === current ? ' aria-current="true"' : '') + '>' + i + '</button>';
    }
    if (current < pages) {
      h += '<button type="button" class="bfau-mr-pg" data-page="' + (current + 1)
        + '" aria-label="Next page">&rsaquo;</button>';
    }
    return h + '</div>';
  }

  /* One button per star level, not a dropdown.
   *
   * A native select has to be opened, picked from and then closed before the
   * choice registers, and on this page that read as the filter not working
   * until you clicked away. Buttons apply on the click that makes them.
   *
   * They also show what is behind each level, which a dropdown cannot: nobody
   * picks "2 stars" to discover it holds nothing. A level with no reviews is
   * rendered disabled rather than hidden, because an absent row invites the
   * question of whether it was filtered out. That is why 1 and 2 are still
   * drawn here, reading 0: the section quotes no reviews at those levels (see
   * SHOWN), and a row saying 0 answers the question that a missing row asks.
   */
  function filterButtons() {
    var h = '<div class="bfau-mr-filters" role="group" aria-label="Filter by rating">';
    h += '<button type="button" class="bfau-mr-f" data-rating="0"'
      + (state.rating === 0 ? ' aria-pressed="true"' : ' aria-pressed="false"')
      + '>All ratings</button>';
    for (var i = 5; i >= 1; i--) {
      var n = HIST[i] || 0;
      h += '<button type="button" class="bfau-mr-f" data-rating="' + i + '"'
        + (state.rating === i ? ' aria-pressed="true"' : ' aria-pressed="false"')
        + (n ? '' : ' disabled')
        + ' aria-label="' + i + ' star' + (i === 1 ? '' : 's') + ', ' + n + ' review'
        + (n === 1 ? '' : 's') + '">'
        + stars(i) + '<span class="bfau-mr-fn">' + n + '</span></button>';
    }
    return h + '</div>';
  }

  /* The heading and the note both depend on whether this product has reviews of
     its own, because the claim being made is different. With its own, the
     section is this fridge's reviews plus its model's; without, every word of it
     is another SKU's and the note has to say so plainly. */
  function headline(hasOwn) {
    return hasOwn
      ? 'Customer reviews for the ' + esc(CHASSIS)
      : 'Reviews for the ' + esc(CHASSIS) + ' — same fridge, different design';
  }

  function note(hasOwn) {
    if (hasOwn) {
      return 'Reviews of this fridge come first, followed by reviews of the same '
        + esc(CHASSIS) + ' cabinet in other finishes and door options — the same '
        + 'cooling, build and running noise. Each review says which model it was '
        + 'written about.';
    }
    return 'This is the same cabinet as our ' + esc(CHASSIS) + ', finished differently. '
      + 'These reviews are from customers who bought other ' + esc(CHASSIS) + ' models '
      + '— so they describe the same cooling, build and running noise, not the '
      + 'design on this one. Each review says which model it was written about.';
  }

  var shellBuilt = false;

  /* Built once. Header, award, note and the controls do not change as a shopper
     pages or filters, and rebuilding them was what broke the select: replacing
     a <select> while its dropdown is open loses the interaction, so the choice
     only landed after a click elsewhere. Now the control outlives its own
     change event. */
  function renderShell() {
    var hasOwn = (streams[0] && streams[0].count) > 0;
    var html = '<div class="bfau-mr-hd"><div class="bfau-mr-hd-l">'
      + '<h3>' + headline(hasOwn) + '</h3>';

    /* Always the pooled score, never the filtered one, so choosing "3 stars"
       cannot make the model look worse than it is. */
    if (RATING) {
      var score = RATING % 1 === 0 ? String(RATING) : RATING.toFixed(1);
      html += '<div class="bfau-mr-rate">' + stars(RATING)
        + '<span class="bfau-mr-score">' + score + '/5</span>'
        + '<img class="bfau-mr-logo" src="' + FEEFO_LOGO + '" alt="Feefo" '
        + 'width="80" height="24" loading="lazy"></div>';
    }
    html += '<p class="bfau-mr-sub">Independent Product Rating &mdash; ' + SHOWN_TOTAL
      + ' verified review' + (SHOWN_TOTAL === 1 ? '' : 's') + ' of this model</p></div>';

    /* BFA's own award, served from their CDN. Removed rather than left torn if
       the file ever moves. */
    html += '<img class="bfau-mr-award" src="' + AWARD
      + '" alt="Feefo Gold Trusted Service Award" loading="lazy" onerror="this.remove()">'
      + '</div>';

    html += '<p class="bfau-mr-note">' + note(hasOwn) + '</p>';
    html += '<hr class="bfau-mr-rule">';

    /* Newest and Oldest, both real. Feefo's widget offers Newest and Popular;
       Popular is not available here — the API has no sort parameter and
       helpful_votes is 0 on nearly every review on this shop, so it would
       reorder nothing. Oldest is available because reading a stream backwards
       needs no help from the API, and it is the more useful of the two anyway on
       a fridge that has been sold for a decade.
       Newest used to sit here on its own as a button that did nothing, which is
       the dead control this replaces. */
    html += '<div class="bfau-mr-ctl"><div class="bfau-mr-sort" role="group" '
      + 'aria-label="Sort reviews">'
      + '<button type="button" class="bfau-mr-tab" data-dir="newest" aria-pressed="'
      + (state.dir === 'newest' ? 'true' : 'false') + '">Newest</button>'
      + '<button type="button" class="bfau-mr-tab" data-dir="oldest" aria-pressed="'
      + (state.dir === 'oldest' ? 'true' : 'false') + '">Oldest</button></div>'
      + filterButtons() + '</div>';

    html += '<div class="bfau-mr-body"></div>';

    root.innerHTML = html;
    root.hidden = false;
    shellBuilt = true;
  }

  function render(rows) {
    if (!shellBuilt) renderShell();
    var n = total();
    var pages = Math.max(1, Math.ceil(n / PER_PAGE));
    var readAll = (streams[0] && streams[0].url) || (streams[1] && streams[1].url);

    /* The whole set, not the slice — "Showing 202 reviews", as the widget words
       it. The page numbers below say which part is on screen. */
    var html = '<p class="bfau-mr-count">'
      + (n ? 'Showing ' + n + ' review' + (n === 1 ? '' : 's')
        + (state.rating ? ' rated ' + state.rating + ' star' + (state.rating === 1 ? '' : 's') : '')
        : 'No reviews at that rating')
      + '</p>';

    html += '<ul class="bfau-mr-list">';
    rows.forEach(function (r) {
      html += '<li class="bfau-mr-item' + (r.own ? ' is-own' : '') + '">'
        + '<div class="bfau-mr-top">' + stars(r.rating)
        + '<span class="bfau-mr-who">' + esc(r.who) + '</span>'
        /* "Trusted Customer - 29 October 2020". The hyphen is the widget's. */
        + '<span class="bfau-mr-when">- ' + esc(when(r.at)) + '</span></div>'
        + (r.text ? '<p class="bfau-mr-text">' + esc(r.text) + '</p>' : '')
        + '<p class="bfau-mr-on">Reviewed on: ' + esc(r.sku)
        + (r.own ? ' (this model)' : '') + '</p></li>';
    });
    html += '</ul>';

    html += '<div class="bfau-mr-foot"><span class="bfau-mr-brand">'
      + '<img src="' + FEEFO_LOGO + '" alt="Feefo" width="80" height="24" loading="lazy">'
      + '<small>Independent Customer Feedback</small></span>'
      + pageButtons(pages, state.page)
      + (readAll ? '<a href="' + esc(readAll) + '" target="_blank" rel="noopener">'
        + 'Read all reviews</a>' : '')
      + '</div>';

    var body = root.querySelector('.bfau-mr-body');
    if (body) body.innerHTML = html;
    root.classList.remove('bfau-mr-busy');
  }

  function show() {
    root.classList.add('bfau-mr-busy');
    slice((state.page - 1) * PER_PAGE, PER_PAGE)
      .then(function (rows) {
        if (!rows.length && state.page === 1 && !state.rating) {
          root.classList.remove('bfau-mr-busy');
          return;                       // nothing anywhere; stay out of the way
        }
        render(rows);
      })
      /* Silent, and the block stays as it was. A failed page turn leaves the
         page the shopper was reading rather than emptying it under them. */
      .catch(function () { root.classList.remove('bfau-mr-busy'); });
  }

  /* Delegated: render() replaces the whole block on every page turn, so a
     handler bound to a button would be thrown away with the button. */
  root.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-page]') : null;
    if (!b) return;
    state.page = Number(b.getAttribute('data-page')) || 1;
    show();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  /* The pressed state moves on the click rather than after the fetch, so a
     button answers immediately and the rows catch up behind it. */
  function press(sel, attr, value) {
    var all = root.querySelectorAll(sel);
    for (var i = 0; i < all.length; i++) {
      all[i].setAttribute('aria-pressed',
        all[i].getAttribute(attr) === value ? 'true' : 'false');
    }
  }

  /* Sort direction. Nothing is re-fetched that has already been seen — the
     chunk cache is keyed on stream, rating and chunk, none of which the
     direction changes; only which end we read from. */
  root.addEventListener('click', function (e) {
    var d = e.target.closest ? e.target.closest('[data-dir]') : null;
    if (!d) return;
    var dir = d.getAttribute('data-dir');
    if (dir === state.dir) return;
    state.dir = dir;
    state.page = 1;
    press('[data-dir]', 'data-dir', dir);
    show();
  });

  /* One click, one filter. No open-pick-close, so nothing to click off. */
  root.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-rating]') : null;
    if (!b || b.disabled) return;
    var v = Number(b.getAttribute('data-rating')) || 0;
    if (v === state.rating) return;
    state.rating = v;
    state.page = 1;
    streams.forEach(function (s) { s.count = null; });   // counts are per filter
    press('[data-rating]', 'data-rating', String(v));
    measure(show);
  });

  /* The pooled rating and count for the header. The bodies response carries a
     summary, but only counts — no average — so the score needs its own lookup,
     and it is the UNFILTERED one on purpose. */
  function poolSummary(pool, then) {
    fetch(SUMMARY + '&product_sku=' + encodeURIComponent(pool), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) {
        var rt = j && j.rating;
        var pr = (rt && rt.product) || {};
        SHOWN_TOTAL = 0;
        for (var i = 1; i <= 5; i++) {
          HIST[i] = isShown(i) ? (pr[i + '_star'] || 0) : 0;
          SHOWN_TOTAL += HIST[i];
        }
        then((rt && rt.rating) || 0);
      })
      /* No score rather than a guessed one. */
      .catch(function () { then(0, 0); });
  }

  /* How many reviews the whole pool has at the current filter, and how many of
     them are this SKU's.
     Unfiltered it costs nothing: the pool summary has already said how many
     reviews sit at each SHOWN level, and one count request for this SKU settles
     the split, because the rest is simply the remainder. Filtered it costs one
     more, since neither figure is known.
     page_size=1 because only summary.meta.count is wanted. */
  function countOf(skus, then) {
    fetch(API + '&product_sku=' + encodeURIComponent(skus) + '&page_size=1&page=1'
      + '&rating=' + ratingParam(), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) { then(((j && j.summary && j.summary.meta) || {}).count || 0); })
      .catch(function () { then(0); });
  }

  function measure(then) {
    countOf(streams[0].skus, function (own) {
      streams[0].count = own;
      if (!state.rating) {
        /* SHOWN_TOTAL, not Feefo's pooled count: `own` came back counted at the
           SHOWN levels only, so the remainder has to be taken from the same
           total, or the chassis stream is handed the 1- and 2-star rows as
           phantom length and the last page of the section comes up short. */
        SET_TOTAL = SHOWN_TOTAL;
        streams[1].count = Math.max(0, SHOWN_TOTAL - own);
        return then();
      }
      countOf(streams[1].skus, function (rest) {
        streams[1].count = rest;
        SET_TOTAL = own + rest;
        then();
      });
    });
  }

  function start(pool) {
    CHASSIS = rootOf(model);
    var all = String(pool).split(',').filter(Boolean);
    var rest = all.filter(function (s) {
      return s.toUpperCase() !== String(sku).toUpperCase();
    });
    streams = [
      { skus: sku, count: null, url: null },
      { skus: rest.join(','), count: null, url: null }
    ];
    poolSummary(pool, function (rating) {
      /* Nothing to show for this model at all -- either no reviews anywhere, or
         none at the levels this section quotes, which is a fridge whose only
         reviews are 1- and 2-star. A zero SHOWN_TOTAL covers both, since every
         level is summed into it. Both stay out of the way rather than drawing a
         header over an empty list. */
      if (!SHOWN_TOTAL) return;
      RATING = rating;
      measure(show);
    });
  }

  /* The pool is baked because a product page cannot work it out: it holds one
     product, and Feefo cannot be asked which SKUs share a chassis. A product too
     new to be in the map falls back to its own SKU, which is what Feefo's widget
     would have shown anyway. */
  fetch(root.getAttribute('data-pools'), { credentials: 'omit' })
    .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
    .then(function (map) { start(poolFor(sku, map)); })
    .catch(function () { start(sku); });
})();
